import React, { useMemo, useEffect } from 'react';
import styles from './ConversationCard.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import { useFormikContext } from 'formik';

const ConversationCard = () => {
  const { values } = useFormikContext();

  function adjustColor(hex, percent) {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    r = Math.min(255, Math.max(0, r + (255 - r) * percent));
    g = Math.min(255, Math.max(0, g + (255 - g) * percent));
    b = Math.min(255, Math.max(0, b + (255 - b) * percent));

    const toHex = (c) => {
      const h = Math.round(c).toString(16);
      return h.length === 1 ? '0' + h : h;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  const chatIconSrc = useMemo(() => {
    const icon = values?.ChatIcon;
    if (!icon) return null;

    if (typeof icon === 'string') {
      if (
        /^data:image\//i.test(icon) ||
        /^https?:\/\//i.test(icon) ||
        icon.startsWith('/')
      ) {
        return icon;
      }
      return `data:image/png;base64,${icon}`;
    }

    if (icon instanceof Blob) {
      return URL.createObjectURL(icon);
    }

    if (icon instanceof ArrayBuffer) {
      const blob = new Blob([icon], { type: 'image/png' });
      return URL.createObjectURL(blob);
    }

    if (icon && icon.buffer instanceof ArrayBuffer) {
      try {
        const blob = new Blob([icon], { type: 'image/png' });
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    }

    if (icon && icon.data) {
      try {
        const arr = Array.isArray(icon.data)
          ? icon.data
          : Array.from(icon.data);
        const u8 = new Uint8Array(arr);
        const type = icon.type || icon.mimeType || 'image/png';
        const blob = new Blob([u8], { type });
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    }

    return null;
  }, [values?.ChatIcon]);

  useEffect(() => {
    return () => {
      if (chatIconSrc && chatIconSrc.startsWith('blob:')) {
        URL.revokeObjectURL(chatIconSrc);
      }
    };
  }, [chatIconSrc]);
  return (
    <div
      className={styles['conversation-card']}
      style={{
        backgroundColor: values?.Theme === 'white' ? '#fff' : '#1a1a1a',
      }}
    >
      <div
        className={styles['card-header']}
        style={{
          backgroundColor:
            values?.Theme === 'white'
              ? values?.Style?.toLowerCase?.() === 'gradient'
                ? '#fff'
                : values?.AccentColor || '#3C3C3C'
              : '#3C3C3C',
          borderRadius: '20px 20px 0 0',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {(() => {
          const pos = values?.TitlePosition?.toLowerCase?.() || 'left';
          const showIcon =
            values?.HeaderStyle === 'Icon + Text' && !!values?.ShowChatIcon;
          const isBasic = values?.Style?.toLowerCase?.() !== 'gradient';

          const Logo = showIcon ? (
            chatIconSrc ? (
              <div
                style={{
                  background:
                    values?.ShowIconBackground && values?.IconBackground
                      ? values?.IconBackground || '#0957D0'
                      : 'transparent',
                }}
                className={styles['circle-box']}
              >
                <img
                  src={chatIconSrc}
                  alt='chat-icon'
                  className={styles['chat-icon']}
                />
              </div>
            ) : (
              <div
                style={{
                  background:
                    values?.ShowIconBackground && values?.IconBackground
                      ? values?.IconBackground || '#0957D0'
                      : 'transparent',
                }}
                className={styles['circle-box']}
              >
                <img
                  src='/src/svg/bot.svg'
                  alt='bot-icon'
                  className={styles['chat-icon']}
                />
              </div>
            )
          ) : null;

          const Info = (
            <SvgWithBranding
              type='info'
              color={
                isBasic
                  ? '#ECECEC'
                  : values?.Theme === 'white'
                  ? '#7e7e7e'
                  : '#ECECEC'
              }
              width={16}
            />
          );

          const TitleText = (
            <p
              className={styles['header-title']}
              style={{
                color: isBasic
                  ? '#fff'
                  : values?.Theme === 'white'
                  ? '#000'
                  : '#fff',
                fontFamily: `${values?.FontFamily}, sans-serif`,
              }}
            >
              {values?.ChatbotName}
            </p>
          );

          const iconColor = isBasic
            ? '#ECECEC'
            : values?.Theme === 'white'
            ? '#7e7e7e'
            : '#fff';
          const actionOrder =
            pos === 'right'
              ? ['minimizeBranding', 'maximizeBranding', 'dots']
              : ['dots', 'maximizeBranding', 'minimizeBranding'];

          const Actions = (
            <div
              className={styles['action-btns-container']}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'flex-end',
              }}
            >
              {actionOrder.map((type) => (
                <SvgWithBranding
                  key={type}
                  type={type}
                  className={styles['actions-icon']}
                  color={iconColor}
                />
              ))}
            </div>
          );

          const TitleRow = (order) => (
            <div
              className={styles['title-container']}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  pos === 'center'
                    ? 'center'
                    : pos === 'right'
                    ? 'flex-end'
                    : 'flex-start',
                gap: 8,
              }}
            >
              {order.map((slot, idx) => {
                const node =
                  slot === 'info' ? Info : slot === 'title' ? TitleText : Logo;
                return (
                  <React.Fragment key={`${slot}-${idx}`}>{node}</React.Fragment>
                );
              })}
            </div>
          );

          if (pos === 'center') {
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  {showIcon ? Logo : null}
                </div>
                <div />
                {Actions}
                <div
                  className={`${styles['title-container']} ${styles['title-abs-center']}`}
                >
                  {TitleText}
                  {Info}
                </div>
              </>
            );
          }

          if (pos === 'right') {
            return (
              <>
                {Actions}
                <div />
                {TitleRow(
                  showIcon ? ['info', 'title', 'logo'] : ['info', 'title']
                )}
              </>
            );
          }

          return (
            <>
              {TitleRow(
                showIcon ? ['logo', 'title', 'info'] : ['title', 'info']
              )}
              <div />
              {Actions}
            </>
          );
        })()}
      </div>

      <div
        className={styles['card-body']}
        style={{
          background:
            values?.Theme === 'white'
              ? values?.Style?.toLowerCase?.() === 'basic'
                ? '#fff'
                : values?.AccentColor
                ? `linear-gradient(
              64deg,
              ${adjustColor(values.AccentColor, 0.5)} 1.03%,
              ${adjustColor(values.AccentColor, 0.6)} 10.86%,
              ${adjustColor(values.AccentColor, 0.7)} 18.67%,
              ${adjustColor(values.AccentColor, 0.8)} 24.44%,
              ${adjustColor(values.AccentColor, 0.9)} 36.52%,
              #ffffff 48.61%,
              ${adjustColor(values.AccentColor, 0.9)} 82.29%,
              ${adjustColor(values.AccentColor, 0.8)} 87.11%,
              ${adjustColor(values.AccentColor, 0.7)} 91.76%,
              ${adjustColor(values.AccentColor, 0.6)} 99.85%
            )`
                : ''
              : '#1A1A1A',
        }}
      >
        <p
          className={styles['body-title']}
          style={{
            color: values?.Theme === 'white' ? '#000' : '#fff',
            fontFamily: `${values?.FontFamily}, sans-serif`,
          }}
        >
          How can
          <span
            className={styles['aiducator-text']}
            style={{
              color: values?.AccentColor ? values?.AccentColor : '',
              fontFamily: `${values?.FontFamily}, sans-serif`,
            }}
          >
            {' '}
            AiDucator{' '}
          </span>
          assist you today?
        </p>
        <div className={styles['messages-container']}>
          <div className={styles['bot-message-container']}>
            {values?.ShowChatIcon &&
              (chatIconSrc ? (
                <div
                  style={{
                    background:
                      values?.ShowIconBackground && values?.IconBackground
                        ? values?.IconBackground || '#0957D0'
                        : 'transparent',
                  }}
                  className={styles['circle-box']}
                >
                  <img
                    src={chatIconSrc}
                    alt='chat-icon'
                    className={styles['chat-icon']}
                  />
                </div>
              ) : (
                <div
                  style={{
                    background:
                      values?.ShowIconBackground && values?.IconBackground
                        ? values?.IconBackground || '#0957D0'
                        : 'transparent',
                  }}
                  className={styles['circle-box']}
                >
                  <img
                    src='/src/svg/bot.svg'
                    alt='bot-icon'
                    className={styles['chat-icon']}
                  />
                </div>
              ))}

            <div className={styles['bot-message']}>
              <p
                className={styles['bot-message-text']}
                style={{
                  color: values?.Theme === 'white' ? '#000' : '#fff',
                  fontFamily: `${values?.FontFamily}, sans-serif`,
                }}
              >
                Hi, I’m AiDucator — your AI guide for learning! I can answer
                your questions and connect you with education experts. I’m
                improving every day, so feel free to ask me things like, “Can I
                talk to a teacher?” or “What is blended learning?”
              </p>
              <p
                className={styles['message-time']}
                style={{
                  color: values?.Theme === 'white' ? '#3c3c3c' : '#BDBDBD',
                  fontFamily: `${values?.FontFamily}, sans-serif`,
                }}
              >
                AiDucator • 9:10 am
              </p>
            </div>
          </div>
          <div className={styles['user-message-container']}>
            <div
              className={styles['user-message-card']}
              style={{
                backgroundColor:
                  values?.Theme === 'white'
                    ? values?.AccentColor
                      ? adjustColor(values.AccentColor, 0.7)
                      : ''
                    : '#616161',
              }}
            >
              <p
                style={{
                  color: values?.Theme === 'white' ? '#1a1a1a' : '#FFFFFF',
                  fontFamily: `${values?.FontFamily}, sans-serif`,
                }}
              >
                What are the features of AiDucator?
              </p>
            </div>

            <p
              className={styles['message-time']}
              style={{
                color: values?.Theme === 'white' ? '#3c3c3c' : '#BDBDBD',
                fontFamily: `${values?.FontFamily}, sans-serif`,
              }}
            >
              Sent: 9:10 am
            </p>
          </div>
        </div>
      </div>
      <div
        className={styles['input-container']}
        style={{
          backgroundColor: values?.Theme === 'white' ? '#ffffff' : '#3C3C3C',
        }}
      >
        <div
          className={styles['input']}
          style={{
            border:
              values?.Theme === 'white'
                ? '1px solid #9e9e9e'
                : '1px solid #ECECEC',
          }}
        >
          <input
            value={values?.PlaceholderText || ''}
            style={{
              backgroundColor:
                values?.Theme === 'white' ? '#ffffff' : '#3C3C3C',
              color: values?.Theme === 'white' ? '#7e7e7e' : '#ECECEC',
              '--plc':
                values?.Theme?.toLowerCase?.() === 'white'
                  ? '#7e7e7e'
                  : '#ECECEC',
              fontFamily: `${values?.FontFamily}, sans-serif`,
            }}
            disabled
            className={styles['message-input']}
          />
          <div className={styles['input-actions']}>
            <SvgWithBranding
              type='mic'
              color={values?.Theme === 'white' ? '#7E7E7E' : '#ECECEC'}
              className={styles['actions-icon']}
            />
            <SvgWithBranding
              type='send'
              color={values?.Theme === 'white' ? '#7E7E7E' : '#ECECEC'}
              className={styles['actions-icon']}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationCard;
